import { Draft, produce } from 'immer';

import {
    ACCEPT_SUGGESTED_ORGANIZATION_IN_EDITOR,
    BULK_UPDATE_ORGANIZATIONS,
    CHANGE_ORGANIZATION_BULK_EDITOR_ACTION_TYPE,
    CHANGE_ORGANIZATION_IN_EDITOR,
    CLOSE_ORGANIZATIONS_BULK_EDITOR,
    COPY_ORGANIZATION_EDITOR,
    DELETE_ORGANIZATION_EDITOR,
    INIT_ORGANIZATIONS_EDITORS,
    LOAD_ORGANIZATION_EDITOR_DATA,
    OPEN_ORGANIZATIONS_BULK_EDITOR,
    OPEN_ORGANIZATIONS_EDITOR,
    OrganizationsEditorAction,
    REJECT_SUGGESTED_ORGANIZATION_IN_EDITOR,
    REMOVE_ORGANIZATION_FROM_EDITOR,
    RESET_ORGANIZATIONS_EDITOR,
    SAVE_ORGANIZATION_TO_EDITOR,
    UPDATE_ORGANIZATION_EDITOR_ERRORS,
    UPDATE_ORGANIZATION_EDITOR_IDS,
    UPDATE_ORGANIZATIONS_EDITORS_DATA,
    UPDATE_ORGANIZATIONS_EDITORS_DATA_STATUS
} from '../actions/organizationsEditor';
import {
    OrganizationEditorParams,
    OrganizationsBulkActionType,
    OrganizationsEditorDataStatus,
    OrganizationsEditorsState,
    OrganizationsEditorState
} from '../typings/organizations';

export const DEFAULT_ORGANIZATIONS_EDITORS_STATE: OrganizationsEditorsState = {};
export const EMPTY_ORGANIZATIONS_EDITOR: OrganizationsEditorState = {
    organizationId: undefined,
    dataStatus: OrganizationsEditorDataStatus.EMPTY,
    isOpen: false
};

export function organizationsEditor(
    state: OrganizationsEditorsState = DEFAULT_ORGANIZATIONS_EDITORS_STATE,
    action: OrganizationsEditorAction
): OrganizationsEditorsState {
    return produce(state, (draft: Draft<OrganizationsEditorsState>) => {
        switch (action.type) {
            case INIT_ORGANIZATIONS_EDITORS: {
                action.params.forEach((editorParams: OrganizationEditorParams) => {
                    const { editorKey, organizationId, suggestedOrganizationId } = editorParams;

                    const editorState: OrganizationsEditorState = {
                        organizationId,
                        suggestedOrganizationId,
                        isOpen: false,
                        dataStatus:
                            organizationId || suggestedOrganizationId
                                ? OrganizationsEditorDataStatus.LOADING
                                : OrganizationsEditorDataStatus.EMPTY
                    };

                    draft[editorKey] = editorState;
                });

                break;
            }

            case UPDATE_ORGANIZATIONS_EDITORS_DATA: {
                const { editorsOrganizations } = action;

                Object.keys(editorsOrganizations).forEach(editorKey => {
                    const organizationData = editorsOrganizations[editorKey];

                    draft[editorKey].dataStatus = organizationData
                        ? OrganizationsEditorDataStatus.LOADED
                        : OrganizationsEditorDataStatus.NOT_FOUND;

                    if (organizationData) {
                        draft[editorKey].errors = undefined;
                    }
                });

                break;
            }

            case UPDATE_ORGANIZATIONS_EDITORS_DATA_STATUS: {
                const { editorsStatuses } = action;

                Object.keys(editorsStatuses).forEach(editorKey => {
                    draft[editorKey].dataStatus = editorsStatuses[editorKey];
                });

                break;
            }

            case UPDATE_ORGANIZATION_EDITOR_ERRORS: {
                const { editorKey, errors } = action;

                draft[editorKey].errors = errors;

                break;
            }

            case OPEN_ORGANIZATIONS_EDITOR: {
                draft[action.editorKey].isOpen = true;

                break;
            }

            case RESET_ORGANIZATIONS_EDITOR: {
                const {
                    editorParams: { editorKey, organizationId }
                } = action;

                // В случае, когда есть ID, но нет данных организации,
                // делаем запрос за данными, для этого выставляем статус LOADING
                draft[editorKey].dataStatus = OrganizationsEditorDataStatus.LOADING;
                draft[editorKey].organizationId = organizationId;
                draft[editorKey].isOpen = false;
                draft[editorKey].errors = undefined;

                break;
            }

            case REMOVE_ORGANIZATION_FROM_EDITOR: {
                const { editorKey } = action;

                draft[editorKey].dataStatus = OrganizationsEditorDataStatus.EMPTY;
                draft[editorKey].organizationId = undefined;
                draft[editorKey].errors = undefined;

                break;
            }

            case SAVE_ORGANIZATION_TO_EDITOR: {
                const { editorKey, organizationId } = action.editorParams;

                draft[editorKey].organizationId = organizationId;
                draft[editorKey].isOpen = false;

                break;
            }

            case CHANGE_ORGANIZATION_IN_EDITOR: {
                const { editorKey, organizationId } = action.editorParams;

                draft[editorKey].organizationId = organizationId;
                draft[editorKey].suggestedOrganizationId = undefined;
                draft[editorKey].dataStatus = organizationId
                    ? OrganizationsEditorDataStatus.LOADING
                    : OrganizationsEditorDataStatus.EMPTY;
                draft[editorKey].errors = undefined;

                break;
            }

            case ACCEPT_SUGGESTED_ORGANIZATION_IN_EDITOR: {
                const { editorKey, organizationId } = action.editorParams;

                draft[editorKey].organizationId = organizationId;
                draft[editorKey].suggestedOrganizationId = undefined;
                draft[editorKey].dataStatus = organizationId
                    ? OrganizationsEditorDataStatus.LOADED
                    : OrganizationsEditorDataStatus.EMPTY;

                break;
            }

            case REJECT_SUGGESTED_ORGANIZATION_IN_EDITOR: {
                const { editorKey } = action.editorParams;

                draft[editorKey].suggestedOrganizationId = undefined;
                draft[editorKey].dataStatus = OrganizationsEditorDataStatus.EMPTY;

                break;
            }

            case LOAD_ORGANIZATION_EDITOR_DATA: {
                const { editorKey, organizationId } = action.editorParams;

                draft[editorKey].dataStatus = organizationId
                    ? OrganizationsEditorDataStatus.LOADING
                    : OrganizationsEditorDataStatus.EMPTY;
                draft[editorKey].errors = undefined;

                break;
            }

            case OPEN_ORGANIZATIONS_BULK_EDITOR: {
                draft[action.editorId] = {
                    isOpen: true,
                    dataStatus: OrganizationsEditorDataStatus.EMPTY,
                    bulkActionType: OrganizationsBulkActionType.EDIT
                };

                break;
            }

            case CLOSE_ORGANIZATIONS_BULK_EDITOR: {
                draft[action.editorId] = EMPTY_ORGANIZATIONS_EDITOR;

                break;
            }

            case CHANGE_ORGANIZATION_BULK_EDITOR_ACTION_TYPE: {
                draft[action.editorId].bulkActionType = action.actionType;

                break;
            }

            case BULK_UPDATE_ORGANIZATIONS: {
                draft[action.editorId].dataStatus = OrganizationsEditorDataStatus.SAVING;

                break;
            }

            case COPY_ORGANIZATION_EDITOR: {
                draft[action.targetEditorId] = state[action.srcEditorId];
                break;
            }

            case UPDATE_ORGANIZATION_EDITOR_IDS: {
                action.organizationEditorUpdatedIds.forEach(item => {
                    draft[item.newId] = {
                        ...draft[item.oldId]
                    };

                    delete draft[item.oldId];
                });

                break;
            }

            case DELETE_ORGANIZATION_EDITOR: {
                draft = DEFAULT_ORGANIZATIONS_EDITORS_STATE;

                break;
            }

            default:
        }

        return draft;
    });
}
